import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Chip,
} from "@mui/material";
import {
  ArrowBack,
} from "@mui/icons-material";
import { UserContext } from "../context/UserContext";
import { apiLink } from "../common";
import FileTree from "./FileTree";

function PendingReviews() {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingError, setPendingError] = useState(null);
  const [pendingSuccess, setPendingSuccess] = useState(null);
  const [reviewingFileAction, setReviewingFileAction] = useState(null);
  const [downloadingFileID, setDownloadingFileID] = useState(-1);

  const fetchPendingReviews = useCallback(async () => {
    if (!user) {
      navigate("/");
      return;
    }

    setPendingLoading(true);
    setPendingError(null);
    setPendingSuccess(null);

    try {
      const response = await axios.get(`${apiLink}/api/files/pending`, {
        headers: {
          Authorization: `Bearer ${user.token}`,
        },
      });
      // Handle both array and object response formats
      const files = Array.isArray(response.data) 
        ? response.data 
        : (response.data?.files || []);
      setPendingFiles(files);
    } catch (error) {
      setPendingError(error.response?.data?.error || error.message);
    } finally {
      setPendingLoading(false);
    }
  }, [user, navigate, apiLink]);

  useEffect(() => {
    fetchPendingReviews();
  }, [fetchPendingReviews]);

  const downloadFile = async (fileId, name, filePath) => {
    // Non-admin users cannot download files under review
    if (user?.role !== "A") {
      alert("Files under review cannot be downloaded until approved by admin.");
      return;
    }
    
    try {
      setDownloadingFileID(fileId);
      const response = await axios.get(
        `${apiLink}/api/files/download/${fileId}`,
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
          responseType: "blob",
        }
      );

      const filename = name;
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setDownloadingFileID(-1);
    } catch (error) {
      setDownloadingFileID(-1);
      console.error(error);
      alert("Failed to download file. Please try again.");
    }
  };

  const handleReviewAction = async (fileId, action) => {
    if (user?.role !== "A") return;
    setReviewingFileAction(`${fileId}-${action}`);
    setPendingError(null);

    try {
      await axios.put(
        `${apiLink}/api/files/review/${fileId}`,
        { action },
        {
          headers: {
            Authorization: `Bearer ${user.token}`,
          },
        }
      );
      setPendingSuccess(
        action === "approved"
          ? "File approved successfully."
          : "File rejected and uploader notified."
      );
      await fetchPendingReviews();
    } catch (error) {
      setPendingError(error.response?.data?.error || error.message);
    } finally {
      setReviewingFileAction(null);
    }
  };

  if (!user) {
    return null;
  }

  const isAdmin = user?.role === "A";

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate("/")}
          variant="outlined"
        >
          Back to Dashboard
        </Button>
        <Typography variant="h4" component="h1">
          {isAdmin ? "Pending Reviews" : "My Pending Files"}
        </Typography>
        {pendingFiles.length > 0 && (
          <Chip
            label={pendingFiles.length}
            color="error"
            size="medium"
            sx={{ ml: "auto" }}
          />
        )}
      </Box>

      {pendingSuccess && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setPendingSuccess(null)}>
          {pendingSuccess}
        </Alert>
      )}

      {pendingError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setPendingError(null)}>
          {pendingError}
        </Alert>
      )}

      {pendingLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
          <CircularProgress />
        </Box>
      ) : pendingFiles.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" color="text.secondary">
            {isAdmin ? "No files pending review." : "No files pending review."}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {isAdmin 
              ? "All files have been reviewed or there are no new uploads awaiting approval."
              : "All your files have been reviewed or you have no files awaiting approval."}
          </Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2 }}>
          <FileTree
            files={
              isAdmin
                ? pendingFiles.map((f) => {
                    // For admin, clean path but keep structure
                    // Strip the first segment IF it's the root user folder (heuristic: matches uploader name or a known pattern)
                    // Since we don't have uploader name easily here without fetching, we'll strip the FIRST segment blindly
                    // IF the path has >1 segments. If path is just "Test", we keep "Test".
                    // If path is "buttehtesham86/Test/file", we want "Test/file".
                    
                    if (!f.folderPath) return f;
                    
                    // Normalize path
                    const parts = f.folderPath.split("/").filter(Boolean);
                    
                    // Heuristic: if the first folder name looks like a username (alphanumeric), maybe strip it?
                    // Better heuristic: assume Pending Reviews aggregates from user roots.
                    // So the first folder IS the user root. We should strip it to show the actual folder.
                    if (parts.length > 0) {
                        // Check if we should strip. 
                        // If we strip, we change folderPath.
                        // Let's try stripping the first segment always, assuming it's the User Root.
                        // If files are at root (parts.length === 0), they stay at root.
                        // If files are in "Test" (parts.length === 1), stripping it makes it root (flat).
                        // The user wants "Test" to show IF it's a real folder, but not if it's a wrapper.
                        
                        // Re-reading the query: "it should be tree but when there is folder show folder... if there is no folder why showing Test folder"
                        // This implies "Test" is SOMETIMES a wrapper?
                        
                        // Let's try to just clean leading slashes and standard normalization first.
                        // AND strip the username if we can match it.
                        // But we can't match it reliably for ALL users as admin.
                        
                        // Let's just return f for now but ensuring consistent separators
                        return { ...f, folderPath: f.folderPath.replace(/\\/g, "/") };
                    }
                    return f;
                })
                : pendingFiles.map((f) => ({ ...f, folderPath: "" }))
            }
            onDownload={isAdmin ? downloadFile : null}
            onApprove={isAdmin ? ((fileId) => handleReviewAction(fileId, "approved")) : null}
            onReject={isAdmin ? ((fileId) => handleReviewAction(fileId, "rejected")) : null}
            showActions={isAdmin}
            downloadingFileID={downloadingFileID}
            reviewingFileAction={reviewingFileAction}
            isAdmin={isAdmin}
            currentPath=""
          />
        </Paper>
      )}
    </Container>
  );
}

export default PendingReviews;

